const MONTH_ALIASES = {
    jan: '1',
    january: '1',
    feb: '2',
    february: '2',
    mar: '3',
    march: '3',
    apr: '4',
    april: '4',
    may: '5',
    jun: '6',
    june: '6',
    jul: '7',
    july: '7',
    aug: '8',
    august: '8',
    sep: '9',
    sept: '9',
    september: '9',
    oct: '10',
    october: '10',
    nov: '11',
    november: '11',
    dec: '12',
    december: '12'
};

const normalizeText = (value = '') =>
    String(value)
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');

const tokenize = (value = '') =>
    normalizeText(value)
        .split(/[\s/_.:-]+/)
        .filter(Boolean);

const getTokenCandidates = (token) => {
    const normalized = normalizeText(token);
    const candidates = new Set([normalized]);

    Object.entries(MONTH_ALIASES).forEach(([monthLabel, monthNumber]) => {
        if (monthLabel.startsWith(normalized)) {
            candidates.add(monthNumber);
        }
    });

    if (MONTH_ALIASES[normalized]) {
        candidates.add(MONTH_ALIASES[normalized]);
    }

    return Array.from(candidates);
};

const addDateTokens = (tokenSet, dayInput, monthInput, yearInput) => {
    const day = String(Number(dayInput));
    const month = String(Number(monthInput));
    const year = String(yearInput || '');

    if (!day || !month || !year) return;

    const day2 = day.padStart(2, '0');
    const month2 = month.padStart(2, '0');

    [
        year,
        month,
        month2,
        day,
        day2,
        `${year}${month2}${day2}`,
        `${year}-${month2}-${day2}`,
        `${day2}-${month2}-${year}`,
        `${day2}.${month2}.${year}`
    ].forEach((t) => tokenSet.add(normalizeText(t)));
};

const buildSearchIndex = (item, formatDate) => {
    const name = normalizeText(item?.name || '');
    const includeDateInSearch = !item?.is_dir;
    const dateText = includeDateInSearch
        ? normalizeText(formatDate(item?.mod_time))
        : '';
    const tokenSet = new Set([...tokenize(name), ...tokenize(dateText)]);

    if (includeDateInSearch && item?.name) {
        const filenameDateMatch = item.name.match(
            /^(\d{1,2})[._\-/](\d{1,2})[._\-/](\d{4})(?:\.[^.]+)?$/
        );

        if (filenameDateMatch) {
            const [, day, month, year] = filenameDateMatch;
            addDateTokens(tokenSet, day, month, year);
        }
    }

    const d =
        includeDateInSearch && item?.mod_time ? new Date(item.mod_time) : null;
    if (d && !Number.isNaN(d.getTime())) {
        const year = String(d.getFullYear());
        const month = String(d.getMonth() + 1);
        const day = String(d.getDate());
        const monthNameLong = normalizeText(
            d.toLocaleString('en-US', { month: 'long' })
        );
        const monthNameShort = normalizeText(
            d.toLocaleString('en-US', { month: 'short' })
        );

        addDateTokens(tokenSet, day, month, year);

        [monthNameLong, monthNameShort].forEach((t) => tokenSet.add(t));
    }

    const tokens = Array.from(tokenSet);

    return {
        blob: `${name} ${dateText} ${tokens.join(' ')}`,
        tokens
    };
};

const matchQuery = (searchIndex, rawQuery = '') => {
    const normalizedQuery = normalizeText(rawQuery).trim();
    if (!normalizedQuery) return true;

    const queryTokenGroups = tokenize(normalizedQuery).map((token) =>
        getTokenCandidates(token)
    );

    if (searchIndex.blob.includes(normalizedQuery)) return true;

    return queryTokenGroups.every((candidates) => {
        return candidates.some(
            (token) =>
                searchIndex.blob.includes(token) ||
                searchIndex.tokens.some((t) => t === token || t.includes(token))
        );
    });
};

export { buildSearchIndex, matchQuery, normalizeText, tokenize, MONTH_ALIASES };
